import api from './api';

class UsersService {
  // Get all users with filters and pagination
  async getUsers(params = {}) {
    try {
      const response = await api.get('/users', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get user by ID
  async getUser(id) {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data.user;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createUser(data) {
    try {
      const response = await api.post('/users', data)

      return response.data;
    } catch (e) {
      throw this.handleError(e)
    }
  }

  // Update user
  async updateUser(id, data) {
    try {
      const response = await api.put(`/users/${id}`, data);
      return response.data.user;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete user
  async deleteUser(id) {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Verify user identity
  async verifyUser(id, data) {
    try {
      const response = await api.put(`/users/${id}/verify`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Block/unblock user
  async toggleUserStatus(id) {
    try {
      const response = await api.put(`/users/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get user bookings
  async getUserBookings(id, params = {}) {
    try {
      const response = await api.get(`/users/${id}/bookings`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get user payments
  async getUserPayments(id, params = {}) {
    try {
      const response = await api.get(`/users/${id}/payments`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Export users
  async exportUsers(params = {}) {
    try {
      const response = await api.get('/users/export', {
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers
      const disposition = response.headers['content-disposition'];
      const filename = disposition 
        ? disposition.split('filename=')[1]?.replace(/['"]/g, '') 
        : 'users_export.csv';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Export completed successfully' };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get user verification status options
  getVerificationStatusOptions() {
    return [
      { value: 'pending', label: 'Pending', color: '#ff9800' },
      { value: 'verified', label: 'Verified', color: '#4caf50' },
      { value: 'rejected', label: 'Rejected', color: '#f44336' },
    ];
  }

  // Get user statistics
  async getUserStats(params = {}) {
    try {
      const response = await api.get('/users/stats', { params });
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning mock data
      if (error.response?.status === 501) {
        console.warn('User stats endpoint not implemented, returning mock data');
        return this.getMockUserStats();
      }
      throw this.handleError(error);
    }
  }

  // Get user status options
  getUserStatusOptions() {
    return [
      { value: 'active', label: 'Active', color: '#4caf50' },
      { value: 'blocked', label: 'Blocked', color: '#f44336' },
      { value: 'suspended', label: 'Suspended', color: '#ff9800' },
    ];
  }

  // Mock user stats for when endpoint is not implemented
  getMockUserStats() {
    return {
      total_users: 1247,
      active_users: 1156,
      blocked_users: 45,
      suspended_users: 12,
      verified_users: 1089,
      new_users_today: 8,
      new_users_this_week: 34,
      new_users_this_month: 157,
      user_growth_percentage: 12.5,
      verification_rate: 87.4,
      login_rate: 73.2
    };
  }

  // Handle API errors
  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    } else if (error.message) {
      return new Error(error.message);
    } else {
      return new Error('An unexpected error occurred');
    }
  }
}

export default new UsersService();